import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import ErrorMessage from 'apis/ErrorMessage';
import {
  abilityApi,
  profileApi,
  fetchDropAbilityList,
} from 'apis/story/abilityApi';
import dayjs from 'dayjs';
import fetchLocalizingInput from 'hooks/fetchLocalizingInput';
import { IDropListProps } from 'utils/types/common';
/**
 * * 작품 관리 - 능력치 & 오토메 프로필 쿼리
 */

interface ICharacter {
  key: number;
  speaker: string;
}
interface ILangProps {
  key: number;
  lang: string;
  ability_name: string;
}

/// 목록
export const useCharacterList = (
  projectId,
  setCharacterList,
  setAbilityList,
) => {
  const { status, data, error, isFetching } = useQuery(
    ['characterData', projectId],
    async () => await abilityApi.read(projectId),
    {
      enabled: !!projectId,

      onSuccess: (data) => {
        const newArr: ICharacter[] = [];
        // console.log(data);
        // console.log(Object.keys(data));
        // console.log(Object.values(data));
        Object.keys(data).map((v, i) => newArr.push({ key: i, speaker: v }));
        setCharacterList(newArr);
        setAbilityList(Object.values(data));
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 능력치 상세 정보
export const useAbilityDetail = (
  projectId: string,
  abilityId: string,
  setDetailData,
  setLangList,
) => {
  const { status, data, error, isFetching } = useQuery(
    ['abilityDetailData', projectId, abilityId],
    async () => await abilityApi.detail(projectId, abilityId),
    {
      enabled: !!projectId,

      onSuccess: (data) => {
        const newLangArr: ILangProps[] = [];
        if (data.langList.length > 0) {
          Object.entries(data?.langList[0]).map((v, i) =>
            newLangArr.push({ lang: v[0], ability_name: String(v[1]), key: i }),
          );
        } else {
          newLangArr.push({ lang: 'KO', ability_name: '', key: 0 });
        }

        // setLangList(newLangArr);
        setDetailData(data.list[0]);
        fetchLocalizingInput(newLangArr, setLangList);
      },
      onError: (err: any) => {
        if (err.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
        return;
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 오토메 프로필 상세 정보
export const useProfileDetail = (
  projectId,
  abilityId,
  setDetailData,
  setLineList,
) => {
  const dateFormat = 'YY-MM-DD';
  const today = dayjs().format(dateFormat);
  const { status, data, error, isFetching } = useQuery(
    ['profileDetailData', projectId, abilityId],
    async () => await profileApi.detail(projectId, abilityId),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        if (data.line_list.length > 0) {
          const newArr = data.line_list.map((v, i) => ({ ...v, key: i }));
          setLineList(newArr);
        } else {
          setLineList([
            {
              key: 0,
              ability_id: -1,
              line_id: -1,
              motion_id: -1,
              condition_type: 0,
              line_condition: null,
              line_text: '',
              motion_name: '',
            },
          ]);
        }
        setDetailData({
          ...data.list[0],
          profile_birth_date:
            data.list[0].profile_birth_date === null
              ? today
              : data.list[0].profile_birth_date,
        });
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 캐릭터 등록
export const useCharacterRegister = (setVisible, setSpeaker) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await abilityApi.characterRegister(props),
    {
      onMutate: (props) => {
        //   console.log('onMutate', props);
      },
      onSuccess: (data) => {
        //   console.log('success', data);
        setVisible(false);
        setSpeaker('');
        queryClient.invalidateQueries(['characterData']);
        message.success('캐릭터가 등록되었습니다', 1);
      },
      onError: (err: any) => {
        setVisible(true);

        if (err.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
        return;
      },
    },
  );
};

/// 캐릭터 수정
export const useCharacterUpdate = (setVisible, setSpeaker) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await abilityApi.characterUpdate(props),
    {
      onMutate: (props) => {
        //   console.log('onMutate', props);
      },
      onSuccess: (data) => {
        setSpeaker('');
        setVisible(false);

        queryClient.invalidateQueries(['abilityData']);
        message.success('캐릭터가 수정되었습니다', 1);
      },
      onError: (err: any) => {
        setVisible(true);
        if (err.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
        return;
      },
    },
  );
};

/// 캐릭터 삭제
export const useCharacterDelete = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await abilityApi.characterDelete(props),
    {
      onMutate: (props) => {
        //   console.log('onMutate', props);
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries(['characterData']);
        message.success('캐릭터가 삭제되었습니다', 1);
      },
      onError: (err: any) => {
        if (err.response) {
          ErrorMessage(err.response.status, err.response.data);
          return;
        }
      },
    },
  );
};

/// 능력치 상세 정보 수정
export const useAbilityUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await abilityApi.abilityUpdate(props),
    {
      onMutate: (props) => {
        //   console.log('onMutate', props);
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries(['abilityData']);
        message.success('능력치가 수정되었습니다', 1);
      },
      onError: (err: any) => {
        if (err.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
        return;
      },
    },
  );
};

/// 프로필 상세 정보 수정
export const useProfileUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(async (props: any) => await profileApi.update(props), {
    onMutate: (props) => {
      //   console.log('onMutate', props);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['abilityData']);
      message.success('프로필이 수정되었습니다', 1);
    },
    onError: (err: any) => {
      if (err.response) {
        ErrorMessage(err.response.status, err.response.data);
      }
      return;
    },
  });
};

/// 연결 캐릭터 - live 일러스트, 일러스트, 미니컷, live 오브젝트
export const useConnectedCharacter = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    [
      'characterDropList',
      projectId, // project id
    ],
    async () => await fetchDropAbilityList.character(projectId),
    {
      enabled: !!projectId,
      // 데이터 재가공
      select: (data) => {
        //     console.log(data);
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({
            key: i,
            value: v.speaker,
            text: v.speaker,
          }),
        );
        newArr.push({ key: newArr.length, value: '', text: '캐릭터 없음' });
        return newArr;
      },
      onSuccess: (data) => {
        // console.log(data);
      },
    },
  );
  return { status, data, error, isFetching };
};
/// 아이콘 드롭다운
export const useIconDropList = (projectId, detailData?: any) => {
  const { status, data, error, isFetching } = useQuery(
    ['iconDropList', projectId],
    async () => await fetchDropAbilityList.icon(projectId),
    {
      enabled: !!projectId,
      // 데이터 재가공
      select: (data) => {
        console.log(detailData);
        const newArr = data.map((v, i) => ({ ...v, key: i }));
        console.log(newArr);
        // 아이콘 드롭다운 데이터에 데이터가 없을 때,
        if (newArr.length <= 0) {
          newArr.push({
            key: 0,
            image_url: detailData?.icon_design_url,
            design_id: detailData?.icon_design_id,
          });
          return newArr;
        }

        // 아이콘 드롭다운 데이터에 데이터는 있지만 디테일 데이터에 있는 아이콘이 없을 때
        if (newArr.length < 0) {
          newArr.map((v, i) =>
            v.design_id === detailData?.icon_design_id
              ? {
                  ...v,
                  key: i,
                  image_url: detailData?.icon_design_url,
                  design_id: detailData?.icon_design_id,
                }
              : v,
          );
        }
        // 둘 다 있을 때는 그냥 리턴

        return newArr;
      },
      onSuccess: (data) => {
        // console.log(data);
      },
    },
  );
  return { status, data, error, isFetching };
};
/// 이모티콘 드롭다운
export const useEmoticonDropList = (projectId, characterName) => {
  const { status, data, error, isFetching } = useQuery(
    ['emoticonDropList', projectId, characterName],
    async () => await fetchDropAbilityList.emoticon(projectId, characterName),
    {
      enabled: !!projectId,
      // 데이터 재가공
      select: (data) => {
        const newArr = data.map((v, i) => ({
          ...v,
          key: i,
          design_id: v.emoticon_slave_id,
        }));

        return newArr;
      },
      onSuccess: (data) => {
        // console.log(data);
      },
    },
  );
  return { status, data, error, isFetching };
};
/// 스탠딩 드롭다운
export const useStandingDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['standingDropList', projectId],
    async () => await fetchDropAbilityList.standing(projectId),
    {
      enabled: !!projectId,
      // 데이터 재가공
      select: (data) => {
        // console.log(data);
        const newArr = data.map((v, i) => ({
          ...v,
          key: i,
        }));
        return newArr;
      },
      onSuccess: (data) => {
        // console.log(data);
      },
    },
  );
  return { status, data, error, isFetching };
};
/// 모션 드롭다운
export const useMotionDropList = (projectId, speaker) => {
  const { status, data, error, isFetching } = useQuery(
    ['motionDropList', projectId, speaker],
    async () => await fetchDropAbilityList.motion(projectId, speaker),
    {
      enabled: !!projectId,
      // 데이터 재가공
      select: (data) => {
        //     console.log(data);
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({
            key: i,
            value: v.motion_id,
            text: v.motion_name,
          }),
        );
        return newArr;
      },
      onSuccess: (data) => {
        // console.log(data);
      },
    },
  );
  return { status, data, error, isFetching };
};
